import { VehicleModelType } from '../enums';
/**
 * 슈퍼자차(= 기존 "고급자차 + 무제한특약"에서 명칭만 변경) 1일 보험료.
 *
 * - 총 보험료 = 1일 요금 × 대여일수 (별도 1일 고정 특례 없음).
 * - 맵에 없는 차종(수입/슈퍼카)은 슈퍼자차 미제공 → 요금 0
 *   (요금 0이면 호출부에서 슈퍼자차 자체가 노출되지 않는다).
 *
 * 제주로 전달 가격표 기준.
 */
export const SUPER_INSURANCE_DAILY_FEE = {
    [VehicleModelType.URBAN]: 15000,
    [VehicleModelType.COMPACT]: 16000,
    [VehicleModelType.MIDSIZE]: 18000,
    [VehicleModelType.SUV]: 32000,
    [VehicleModelType.EV]: 32000,
    [VehicleModelType.RV]: 35000,
    [VehicleModelType.LUXURY]: 29000, // 고급 (제네시스 제외 — 제네시스는 대형/프리미엄 오버라이드)
};
/** 대형/프리미엄(GV/쏠라티) 1일 요금 (차종과 무관하게 지정 모델에 적용) */
export const SUPER_INSURANCE_PREMIUM_DAILY_FEE = 59000;
/**
 * 대형/프리미엄 차량모델 ID. 차종(SUV/RV)과 무관하게 우선 적용(→ 59,000).
 *
 * 대상 (2026-07 API 조회 기준):
 *   쏠라티(15인/경유) 18/19~26년: 1846, 1922, 1949, 2111, 2254
 *   GV70 21/23/25년:            1885, 1941, 2133
 *   GV80 21/23/25년(+25 쿠페):   1897, 1936, 2120, 2103
 *
 * ⚠️ 차량(모델) 교체·재등록으로 ID가 바뀌면 이 목록만 갱신하면 된다.
 */
export const SUPER_INSURANCE_PREMIUM_MODEL_IDS = [
    1846,
    1922,
    1949,
    2111,
    2254,
    1885,
    1941,
    2133,
    1897,
    1936,
    2120,
    2103, // GV80 (2103 = 25년 쿠페)
];
/**
 * 차종별 슈퍼자차 요금이 적용되는 회사 = 제주로(companyId 7) 전용.
 * 다른 D2C 브랜드(family/rainbow 등)는 기존 보험료 로직을 그대로 유지한다.
 */
export const SUPER_INSURANCE_COMPANY_ID = 7;
/**
 * 차량의 슈퍼자차 1일 보험료를 반환한다.
 *
 * - 제주로(companyId 7)가 아니면 `null` → 호출부는 기존 로직 유지
 * - 제주로:
 *   · 대형/프리미엄 지정 모델(GV/쏠라티) → 59,000 (차종 무시)
 *   · 매핑된 차종 → 차종별 요금
 *   · 전기(EV) → SUV와 동일 가격
 *   · 미매핑 차종(수입/슈퍼카) → 0 (슈퍼자차 미제공)
 */
export const getSuperInsuranceDailyFee = (params) => {
    var _a;
    const { companyId, type, vehicleModelId } = params;
    if (companyId !== SUPER_INSURANCE_COMPANY_ID)
        return null;
    if (vehicleModelId != null &&
        SUPER_INSURANCE_PREMIUM_MODEL_IDS.includes(vehicleModelId)) {
        return SUPER_INSURANCE_PREMIUM_DAILY_FEE;
    }
    return (_a = SUPER_INSURANCE_DAILY_FEE[type]) !== null && _a !== void 0 ? _a : 0;
};
/**
 * 차종별 슈퍼자차 ERP 기타상품(ExtraItem) 이름.
 *
 * 예약 생성 시 이 이름으로 상품을 매칭한다. 요금표(SUPER_INSURANCE_DAILY_FEE)와
 * 동일한 차종 분기를 쓰며, ERP에 등록된 실제 상품명과 정확히 일치해야 한다
 * (한 글자라도 다르면 매칭 실패 → 회사 기본 기타상품으로 폴백).
 * - SUV·EV는 동일 상품(`슈퍼자차_SUV/전기`)을 공유한다.
 */
export const SUPER_INSURANCE_EXTRA_ITEM_NAME = {
    [VehicleModelType.URBAN]: '슈퍼자차_경차',
    [VehicleModelType.COMPACT]: '슈퍼자차_준중형',
    [VehicleModelType.MIDSIZE]: '슈퍼자차_중형',
    [VehicleModelType.SUV]: '슈퍼자차_SUV/전기',
    [VehicleModelType.EV]: '슈퍼자차_SUV/전기',
    [VehicleModelType.RV]: '슈퍼자차_승합',
    [VehicleModelType.LUXURY]: '슈퍼자차_고급',
};
/** 대형/프리미엄(GV/쏠라티) 슈퍼자차 기타상품 이름 (차종 무관, 지정 모델 우선) */
export const SUPER_INSURANCE_PREMIUM_EXTRA_ITEM_NAME = '슈퍼자차_GV/쏠라티';
/**
 * 차량의 슈퍼자차 ERP 기타상품 이름을 반환한다.
 * (getSuperInsuranceDailyFee와 완전히 동일한 회사·프리미엄·차종 분기)
 *
 * - 제주로(companyId 7)가 아니면 `null`
 * - 대형/프리미엄 지정 모델 → `슈퍼자차_GV/쏠라티` (차종 무시)
 * - 매핑된 차종 → 차종별 상품명 (EV는 SUV와 동일)
 * - 미매핑 차종(수입/슈퍼카) → `null` (슈퍼자차 미제공)
 */
export const getSuperInsuranceExtraItemName = (params) => {
    var _a;
    const { companyId, type, vehicleModelId } = params;
    if (companyId !== SUPER_INSURANCE_COMPANY_ID)
        return null;
    if (vehicleModelId != null &&
        SUPER_INSURANCE_PREMIUM_MODEL_IDS.includes(vehicleModelId)) {
        return SUPER_INSURANCE_PREMIUM_EXTRA_ITEM_NAME;
    }
    return (_a = SUPER_INSURANCE_EXTRA_ITEM_NAME[type]) !== null && _a !== void 0 ? _a : null;
};
